import React, { useState, useEffect } from 'react'

import { BodyText } from '@hm-group/fabric-components'
import {
  MyPointsIcon,
  AccountDefaultIcon,
  SettingsIcon,
  DeliveryIcon,
} from '@hm-group/fabric-icons'
import {
  IconLinksListWithDivider,
  WelcomePointsIcon,
  PersonIcon,
  ScreenReaderOnlyText,
  HeadingXSmall,
} from '@pb/components'
import { getLabelFromStorage, trackEvent } from '@pb/utils'
import cn from 'classnames'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { ACCOUNT_ROUTES, EXTERNAL_ROUTES } from 'App/constants'
import appSelectors from 'App/duck/selectors'
import optimizelyConstants from 'constants/optimizely'
import { ACCOUNT_TRACKING_EVENTS } from 'features/account/constants'
import useOptimizelyDecision from 'features/account/hooks/useOptimizelyDecision'
import purchasesSelectors from 'features/account/purchases/duck/selectors'
import settingsSelectors from 'features/account/settings/duck/selectors'

import styles from './IconLinkList.module.scss'

const IconLinkList: React.FC = () => {
  const [showSettingsBadge, setShowSettingBadge] = useState(false)
  const [showActiveOrderBadge, setShowActiveOrderBadge] = useState(false)

  const onlineActiveOrders = useSelector(purchasesSelectors.getOnlineActiveOrders)
  const shouldShowCompleteProfileReminder = useSelector(
    settingsSelectors.getShowCompleteProfileReminder,
  )
  const isEmailConfirmationStateUnconfirmed = useSelector(
    appSelectors.getIsEmailConfirmationStateUnconfirmed,
  )
  const IsUserFullMember = useSelector(appSelectors.getUserIsFullMember)
  const isNorthstarBrand = useOptimizelyDecision(optimizelyConstants.ACCOUNT_NORTHSTAR_VARIATION)

  useEffect(() => {
    setShowActiveOrderBadge(!!onlineActiveOrders.length)
    setShowSettingBadge(isEmailConfirmationStateUnconfirmed || shouldShowCompleteProfileReminder)
  }, [isEmailConfirmationStateUnconfirmed, shouldShowCompleteProfileReminder, onlineActiveOrders])

  const renderIcon = (icon, label, badgeText = '', shouldShowBadge = false): React.ReactNode => {
    return (
      <div className={cn(!isNorthstarBrand && styles.textWithIcon)}>
        <div className={cn(styles.iconContainer, isNorthstarBrand && styles.iconContainerNS)}>
          {icon}
          {shouldShowBadge && (
            <span
              className={cn(styles.hasTopBadge, isNorthstarBrand && styles.hasTopBadgeNS)}
              title={badgeText}
            >
              <ScreenReaderOnlyText>{badgeText}</ScreenReaderOnlyText>
            </span>
          )}
        </div>

        {!isNorthstarBrand ? (
          <HeadingXSmall Tag="span" className={styles.textWrap} marginless>
            {label}
          </HeadingXSmall>
        ) : (
          <BodyText Tag="span" className={styles.textWrap} isMarginless>
            {label}
          </BodyText>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        styles.iconLinkListContainer,
        isNorthstarBrand && styles.iconLinkListContainerNS,
      )}
    >
      <IconLinksListWithDivider
        className={cn(!isNorthstarBrand && styles.listItem)}
        divider={isNorthstarBrand ? 'hide' : 'show'}
        iconLinkList={[
          {
            to: ACCOUNT_ROUTES.Purchases?.path,
            Tag: Link,
            iconWithText: renderIcon(
              <DeliveryIcon />,
              getLabelFromStorage('account.menu.purchases'),
              getLabelFromStorage('account.accessibility.active.order'),
              showActiveOrderBadge,
            ),
            onClick: () => {
              trackEvent({
                ...ACCOUNT_TRACKING_EVENTS.allPurchasesEntry,
                event_label: 'from top menu',
              })
            },
          },
          {
            to: ACCOUNT_ROUTES.Settings?.path,
            Tag: Link,
            iconWithText: renderIcon(
              <SettingsIcon />,
              getLabelFromStorage('account.topmenu.mysettings'),
              getLabelFromStorage('account.accessibility.complete.information'),
              showSettingsBadge,
            ),
            onClick: () => {
              trackEvent({
                ...ACCOUNT_TRACKING_EVENTS.settingsEntry,
                event_label: 'from top menu',
              })
            },
          },
        ]}
      />
      {IsUserFullMember && (
        <IconLinksListWithDivider
          className={cn(!isNorthstarBrand && styles.listItem, styles.listItemRow2)}
          divider={isNorthstarBrand ? 'hide' : 'show'}
          iconLinkList={[
            {
              href: EXTERNAL_ROUTES.clubInfoPage,
              Tag: 'a',
              iconWithText: renderIcon(
                !isNorthstarBrand ? <PersonIcon /> : <AccountDefaultIcon />,
                getLabelFromStorage('account.menu.info'),
              ),
              onClick: () => {
                trackEvent({
                  ...ACCOUNT_TRACKING_EVENTS.aboutMembersshipEntry,
                  event_label: 'from top menu',
                })
              },
            },
            {
              to: ACCOUNT_ROUTES.PointsHistory?.path,
              Tag: Link,
              iconWithText: renderIcon(
                !isNorthstarBrand ? <WelcomePointsIcon /> : <MyPointsIcon />,
                getLabelFromStorage('account.menu.pointshistory'),
              ),
              onClick: (): void => {
                trackEvent({
                  ...ACCOUNT_TRACKING_EVENTS.pointsHistoryUserEntry,
                  event_label: 'from top menu',
                })
              },
            },
          ]}
        />
      )}
    </div>
  )
}

export default IconLinkList
